// Display vocabulary for the service-user HFP report.
//
// The ratified copy lives in the Figma file "RMS · Holistic Functional
// Profile" (READY FOR DEV pages, 8 Sep 2026). Display names here supersede
// the older crosswalk suNames; anything the payload carries that the design
// does not name falls back to the crosswalk name so no area can silently
// disappear from a child's report.
import { FAMILY_WORDS, FAMILY_ENV_WORDS } from '../../utils/hfpV3ReportAdapter';

// ---------------------------------------------------------------------------
// Pronouns (static UI copy only — AI narratives arrive personalised from the
// backend, which resolves the same pronoun set server-side).
// ---------------------------------------------------------------------------
export const PRONOUNS = {
  'he/him': { subj: 'he', obj: 'him', poss: 'his', plural: false },
  'she/her': { subj: 'she', obj: 'her', poss: 'her', plural: false },
  'they/them': { subj: 'they', obj: 'them', poss: 'their', plural: true },
  'ze/zir': { subj: 'ze', obj: 'zir', poss: 'zir', plural: false },
};

export function pronounForms(pronounSet) {
  return PRONOUNS[pronounSet] || PRONOUNS['they/them'];
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
export function numberWord(n) {
  return NUMBER_WORDS[n] || String(n);
}

// ---------------------------------------------------------------------------
// Levels. Ratified words + meanings (S-L sheet / meaning modals, 8 Sep).
// Meaning never rides on colour alone: every level = word + mark + colour.
// ---------------------------------------------------------------------------
export const LEVELS = {
  priority: {
    key: 'priority',
    word: 'Priority area',
    meaning: 'Support matters most here. It does not mean something is wrong.',
  },
  support: {
    key: 'support',
    word: 'Support need',
    meaning: 'Some help or small changes here could make a real difference.',
  },
  well: {
    key: 'well',
    word: 'Going well',
    meaning: 'Celebrate it. These strengths can help in other areas too.',
  },
  none: {
    key: 'none',
    word: 'Nothing recorded yet',
    meaning: 'This has not been answered yet. It can still be filled in.',
  },
};

// Levels are derived from the adapter's rating enum, via the exact family-word
// strings it exports — never from retyped literals, so a copy change to a
// family word cannot silently degrade an area to "Nothing recorded yet".
const RATING_TO_LEVEL = {
  strength: 'well',
  none: 'well',
  support: 'support',
  priority: 'priority',
  insufficient: 'none',
  notApplicable: 'none',
};
const FAMILY_WORD_TO_LEVEL = {};
Object.entries(FAMILY_WORDS).forEach(([rating, word]) => {
  FAMILY_WORD_TO_LEVEL[word.toLowerCase()] = RATING_TO_LEVEL[rating];
});
Object.entries(FAMILY_ENV_WORDS).forEach(([rating, word]) => {
  FAMILY_WORD_TO_LEVEL[word.toLowerCase()] = RATING_TO_LEVEL[rating];
});
const NOT_ASKED_WORD = FAMILY_WORDS.notApplicable.toLowerCase();

/**
 * Map an adapter family word (e.g. "Priority area", "Barriers present") to a
 * design level key. Unknown words read as "none" — a gap, never a judgement.
 * `notAsked` marks the "this side was not asked" variant of Nothing recorded.
 */
export function levelFromFamilyWord(word) {
  const normalised = String(word || '').trim().toLowerCase();
  const key = FAMILY_WORD_TO_LEVEL[normalised] || 'none';
  const notAsked = normalised === NOT_ASKED_WORD;
  return { key, notAsked };
}

// Adjustment groups arrive with bucket strings from the matcher.
export function levelFromBucket(bucket) {
  const b = String(bucket || '').toLowerCase();
  if (b.includes('priority')) return 'priority';
  if (b.includes('support')) return 'support';
  return 'support';
}

// ---------------------------------------------------------------------------
// Sections (the seven profile sections, in ratified order).
// `internal` matches the adapter/report-model domain keys.
// ---------------------------------------------------------------------------
export const SECTIONS = [
  {
    key: 'executive-functions',
    internal: 'Executive Function',
    title: 'Executive Functions',
    lede: ({ name, p }) => `Mental skills that help ${name} plan, focus, and achieve ${p.poss} goals`,
  },
  {
    key: 'sensory',
    internal: 'Sensory',
    title: 'Sensory',
    lede: ({ name, p, areaCount }) =>
      `How ${name} takes in what ${p.subj} ${p.plural ? 'see, hear, touch and feel' : 'sees, hears, touches and feels'}. ` +
      `${numberWord(areaCount).replace(/^./, (c) => c.toUpperCase())} senses, one at a time.`,
  },
  {
    key: 'social-emotional',
    internal: 'Social & Emotional',
    title: 'Social & Emotional',
    lede: ({ name }) => `How ${name} understands and manages feelings and relationships`,
  },
  {
    key: 'movement-coordination',
    internal: 'Motor & Physical',
    title: 'Movement & Coordination',
    lede: ({ name }) => `Physical skills that help ${name} move, write, and explore the world`,
  },
  {
    key: 'speech-language',
    internal: 'Speech, Language & Communication',
    title: 'Speech, Language & Communication',
    lede: ({ name }) => `How ${name} understands, uses, and expresses language`,
  },
  {
    key: 'other-areas',
    internal: 'Global Participation Indicators',
    title: 'Other Areas of Life',
    lede: ({ name }) => `How ${name} manages and participates in daily life activities. Some areas are only asked of one place.`,
  },
  {
    key: 'environmental',
    internal: 'Environmental Factors',
    title: 'Environmental Factors',
    lede: ({ name }) =>
      `How ${name}’s environment supports or constrains everyday functioning. ` +
      'A dashed circle means nothing was recorded, not that things are fine.',
  },
];

// Desktop section-row chip labels (shortened per the D/Section row component).
export const SECTION_CHIP_LABELS = {
  'executive-functions': 'Executive Functions',
  sensory: 'Sensory',
  'social-emotional': 'Social & Emotional',
  'movement-coordination': 'Movement',
  'speech-language': 'Speech & Language',
  'other-areas': 'Other Areas of Life',
  environmental: 'Environmental',
};

// ---------------------------------------------------------------------------
// Area display names, keyed by the payload's internal subdomain/sense keys,
// with the crosswalk suName as an accepted alias. Order follows the frames.
// ---------------------------------------------------------------------------
export const AREA_NAMES = {
  'Executive Function': [
    { keys: ['Attention', 'Staying focused'], name: 'Staying focused' },
    { keys: ['Cognitive Flexibility', 'Adapting and solving problems', 'Changing plans'], name: 'Adapting to changes' },
    { keys: ['Managing Impulses', 'Stopping and thinking'], name: 'Stopping and thinking before acting' },
    { keys: ['Planning, Sequencing & Organisation', 'Planning and organising'], name: 'Planning and organising tasks' },
    { keys: ['Memory', 'Remembering things'], name: 'Holding information in mind' },
  ],
  Sensory: [
    { keys: ['Visual', 'Sight'], name: 'Seeing' },
    { keys: ['Auditory', 'Hearing'], name: 'Hearing' },
    { keys: ['Tactile', 'Touch'], name: 'Touch' },
    { keys: ['Taste'], name: 'Taste' },
    { keys: ['Smell'], name: 'Smell' },
    { keys: ['Proprioception', 'Body position'], name: 'Body position' },
    { keys: ['Vestibular', 'Balance'], name: 'Balance' },
    { keys: ['Spatial', 'Judging space'], name: 'Judging space' },
    { keys: ['Body & Space', 'Body and movement'], name: 'Body and movement' },
    { keys: ['Interoception', 'Body signals'], name: 'Inner signals' },
  ],
  'Social & Emotional': [
    { keys: ['Forming Relationships', 'Relationships'], name: 'Making and keeping relationships' },
    { keys: ['Navigating Social Interactions', 'Getting on with others'], name: 'Getting along with others' },
    { keys: ['Social Cognition'], name: 'Understanding social situations' },
    { keys: ['Understanding and Managing Emotions', 'Feelings'], name: 'Understanding and managing feelings' },
  ],
  'Motor & Physical': [
    { keys: ['Fine Motor', 'Using their hands'], name: 'Using hands for small tasks' },
    { keys: ['Gross Motor', 'Whole-body movement'], name: 'Whole-body movement' },
  ],
  'Speech, Language & Communication': [
    { keys: ['Generating Sound', 'Speaking clearly'], name: 'Speaking clearly' },
    { keys: ['Understanding and Using Words and Sentences', 'Understanding and using words'], name: 'Understanding and using words' },
    { keys: ['Use of Language, Gesture, Signs and Symbols', 'Body language and gestures'], name: 'Using gestures and body language' },
  ],
  'Global Participation Indicators': [
    { keys: ['Behaviour', 'Getting started and keeping going'], name: 'Behaviour' },
    { keys: ['Daily Living and Self-Care', 'Everyday self-care'], name: 'Daily living and self-care' },
    { keys: ['Energy and Drive', 'Energy'], name: 'Energy and motivation' },
    { keys: ['Learning and Applying Knowledge', 'Learning'], name: 'Learning' },
    { keys: ['Physical Needs', 'Sleep and eating'], name: 'Sleep and physical needs' },
    { keys: ['School Participation', 'Being at school'], name: 'Being at school' },
  ],
  'Environmental Factors': [
    { keys: ['Physical Environment', 'Places they spend time'], name: 'Physical Environment' },
    { keys: ['Products and Technology', 'Things they use'], name: 'Products and Technology' },
    { keys: ['Support and Relationships', 'People who support them'], name: 'Support and Relationships' },
    { keys: ['Attitudes', 'How others treat them'], name: 'Attitudes' },
    { keys: ['Services, Systems and Policies', 'Services and support'], name: 'Services, Systems and Policies' },
  ],
};

/**
 * Resolve an area's display name and its position in the ratified order.
 * Unmatched keys keep their given name and sort after the named ones.
 */
export function areaDisplay(internalDomain, key) {
  const list = AREA_NAMES[internalDomain] || [];
  const lower = String(key || '').trim().toLowerCase();
  for (let i = 0; i < list.length; i += 1) {
    if (list[i].keys.some((k) => k.toLowerCase() === lower) || list[i].name.toLowerCase() === lower) {
      return { name: list[i].name, order: i, unmatched: false };
    }
  }
  return { name: key, order: list.length, unmatched: true };
}

/** Match an adjustment group's area name back to a section area by display name. */
export function normaliseAreaName(name) {
  const lower = String(name || '').trim().toLowerCase();
  for (const domain of Object.keys(AREA_NAMES)) {
    for (const entry of AREA_NAMES[domain]) {
      if (entry.keys.some((k) => k.toLowerCase() === lower) || entry.name.toLowerCase() === lower) {
        return entry.name;
      }
    }
  }
  return name;
}

// ---------------------------------------------------------------------------
// Adjustment category tags (RA type labels from the matcher).
// ---------------------------------------------------------------------------
export const CATEGORY_TAGS = {
  Environmental: { label: 'Environmental', surfaceVar: '--hfp-surface-cat-environmental', inkVar: '--hfp-ink-cat-environmental' },
  Scaffolding: { label: 'Scaffolding', surfaceVar: '--hfp-surface-cat-scaffolding', inkVar: '--hfp-ink-cat-scaffolding' },
  'Skill-building': { label: 'Skill-building', surfaceVar: '--hfp-surface-cat-skill', inkVar: '--hfp-ink-cat-skill' },
};

export const LEGAL_NOTICE_TEXT =
  'This is not a medical assessment and does not constitute a diagnosis, and cannot be used to claim or deny any medical condition.';
